import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { AppStore } from './types';
import {
  createAuthSlice,
  createStudentsSlice,
  createWhiteboardSlice,
  createWordBankSlice,
  createWhiteboardConfigSlice,
  createSessionSlice,
  createWorkspaceSlice,
  createClassroomToolsSlice,
  createCanvasSlice,
} from './slices';

/**
 * Current store version - increment when making breaking schema changes
 */
const STORE_VERSION = 2;

/**
 * Migrate persisted state from older versions
 * This prevents data loss when the schema changes
 */
function migratePersistedState(persistedState: unknown, version: number): unknown {
  // Type guard for state object
  if (!persistedState || typeof persistedState !== 'object') {
    return persistedState;
  }

  const state = persistedState as Record<string, unknown>;

  // Migration from version 1 to 2: Add 'type' field to savedPresentations
  if (version < 2) {
    if (Array.isArray(state.savedPresentations)) {
      state.savedPresentations = state.savedPresentations.map((pres: Record<string, unknown>) => {
        // If presentation doesn't have a type, it's an old Google Slides presentation
        if (!pres.type) {
          return {
            ...pres,
            type: 'google-slides',
          };
        }
        return pres;
      });
    }
  }

  // Add future migrations here as needed:
  // if (version < 3) { ... }

  return state;
}

/**
 * Main application store combining all slices
 * Features:
 * - DevTools integration for debugging (only in development)
 * - Local storage persistence for session continuity
 * - TypeScript types for all state
 */
export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      (...args) => ({
        ...createAuthSlice(...args),
        ...createStudentsSlice(...args),
        ...createWhiteboardSlice(...args),
        ...createWordBankSlice(...args),
        ...createWhiteboardConfigSlice(...args),
        ...createSessionSlice(...args),
        ...createWorkspaceSlice(...args),
        ...createClassroomToolsSlice(...args),
        ...createCanvasSlice(...args),
      }),
      {
        name: 'wbw4-store',
        version: STORE_VERSION,
        storage: createJSONStorage(() => localStorage),
        migrate: migratePersistedState,
        partialize: (state) => ({
          // Persist auth state (except loading/error)
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          // Persist students
          students: state.students,
          selectedStudentId: state.selectedStudentId,
          // Persist whiteboard elements and settings
          elements: state.elements,
          zoom: state.zoom,
          panX: state.panX,
          panY: state.panY,
          tool: state.tool,
          // Persist word banks
          wordBanks: state.wordBanks,
          activeWordBankId: state.activeWordBankId,
          // Persist whiteboard configuration (WB-01, WB-02 settings)
          wb01Config: state.wb01Config,
          wb02Config: state.wb02Config,
          presets: state.presets,
          activePresetId: state.activePresetId,
          // Persist session state
          userType: state.userType,
          teacherId: state.teacherId,
          animalCode: state.animalCode,
          deviceId: state.deviceId,
          sessionExpiry: state.sessionExpiry,
          lastTeacherLogin: state.lastTeacherLogin,
          studentLoginEnabled: state.studentLoginEnabled,
          // Persist workspace state
          recentAppIds: state.recentAppIds,
          favoriteAppIds: state.favoriteAppIds,
          viewMode: state.viewMode,
          savedPresentations: state.savedPresentations,
          // Persist classroom tools state
          timerDuration: state.timerDuration,
          timerRemaining: state.timerRemaining,
          timerIsRunning: state.timerIsRunning,
          timerStartedAt: state.timerStartedAt,
          timerChimeEnabled: state.timerChimeEnabled,
          activeSymbol: state.activeSymbol,
          trafficLightState: state.trafficLightState,
          stopwatchElapsed: state.stopwatchElapsed,
          stopwatchIsRunning: state.stopwatchIsRunning,
          stopwatchStartedAt: state.stopwatchStartedAt,
          stopwatchLaps: state.stopwatchLaps,
          // Persist canvas state
          widgets: state.widgets,
          canvasMode: state.canvasMode,
          gridSnap: state.gridSnap,
          showGrid: state.showGrid,
          gridSize: state.gridSize,
          backgroundColor: state.backgroundColor,
          backgroundImage: state.backgroundImage,
          savedLayouts: state.savedLayouts,
          nextZIndex: state.nextZIndex,
          currentLayoutId: state.currentLayoutId,
        }),
      }
    ),
    {
      name: 'wbw4-store',
      enabled: import.meta.env.DEV,
    }
  )
);

// Selector hooks for better performance
// All hooks use useShallow to prevent infinite re-renders
export const useAuth = () =>
  useAppStore(
    useShallow((state) => ({
      user: state.user,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      error: state.error,
      setUser: state.setUser,
      setLoading: state.setLoading,
      setError: state.setError,
      login: state.login,
      logout: state.logout,
    }))
  );

export const useStudents = () =>
  useAppStore(
    useShallow((state) => ({
      students: state.students,
      selectedStudentId: state.selectedStudentId,
      isLoading: state.isLoading,
      error: state.error,
      setStudents: state.setStudents,
      addStudent: state.addStudent,
      updateStudent: state.updateStudent,
      removeStudent: state.removeStudent,
      selectStudent: state.selectStudent,
      setStudentsLoading: state.setStudentsLoading,
      setStudentsError: state.setStudentsError,
    }))
  );

export const useWhiteboard = () =>
  useAppStore(
    useShallow((state) => ({
      elements: state.elements,
      selectedElementId: state.selectedElementId,
      zoom: state.zoom,
      panX: state.panX,
      panY: state.panY,
      isDrawing: state.isDrawing,
      tool: state.tool,
      addElement: state.addElement,
      updateElement: state.updateElement,
      removeElement: state.removeElement,
      selectElement: state.selectElement,
      setZoom: state.setZoom,
      setPan: state.setPan,
      setTool: state.setTool,
      setIsDrawing: state.setIsDrawing,
      clearWhiteboard: state.clearWhiteboard,
    }))
  );

export const useWordBank = () =>
  useAppStore(
    useShallow((state) => ({
      wordBanks: state.wordBanks,
      activeWordBankId: state.activeWordBankId,
      selectedWordIds: state.selectedWordIds,
      isLoading: state.isLoading,
      error: state.error,
      setWordBanks: state.setWordBanks,
      addWordBank: state.addWordBank,
      updateWordBank: state.updateWordBank,
      removeWordBank: state.removeWordBank,
      setActiveWordBank: state.setActiveWordBank,
      addWordToBank: state.addWordToBank,
      removeWordFromBank: state.removeWordFromBank,
      selectWords: state.selectWords,
      toggleWordSelection: state.toggleWordSelection,
      clearWordSelection: state.clearWordSelection,
      setWordBankLoading: state.setWordBankLoading,
      setWordBankError: state.setWordBankError,
    }))
  );

export const useWhiteboardConfig = () =>
  useAppStore(
    useShallow((state) => ({
      // WB-01 config
      wb01Config: state.wb01Config,
      setWB01BoxCount: state.setWB01BoxCount,
      setWB01HiddenLetters: state.setWB01HiddenLetters,
      toggleWB01Letter: state.toggleWB01Letter,
      setWB01EnabledDigraphs: state.setWB01EnabledDigraphs,
      toggleWB01Digraph: state.toggleWB01Digraph,
      setWB01EnabledVowelTeams: state.setWB01EnabledVowelTeams,
      toggleWB01VowelTeam: state.toggleWB01VowelTeam,
      setWB01TargetWord: state.setWB01TargetWord,
      clearWB01TargetWord: state.clearWB01TargetWord,
      setWB01LearningMode: state.setWB01LearningMode,
      setWB01ActiveWord: state.setWB01ActiveWord,
      resetWB01Config: state.resetWB01Config,

      // WB-02 config
      wb02Config: state.wb02Config,
      setWB02TapRegionCount: state.setWB02TapRegionCount,
      setWB02GuideLinesEnabled: state.setWB02GuideLinesEnabled,
      setWB02GuideLineTypes: state.setWB02GuideLineTypes,
      toggleWB02GuideLine: state.toggleWB02GuideLine,
      setWB02TargetWord: state.setWB02TargetWord,
      clearWB02TargetWord: state.clearWB02TargetWord,
      resetWB02Config: state.resetWB02Config,

      // Preset management
      presets: state.presets,
      activePresetId: state.activePresetId,
      savePreset: state.savePreset,
      loadPreset: state.loadPreset,
      deletePreset: state.deletePreset,
      updatePresetName: state.updatePresetName,

      // Reset all
      resetAllConfig: state.resetAllConfig,
    }))
  );

export const useSession = () =>
  useAppStore(
    useShallow((state) => ({
      // State
      user: state.user,
      userType: state.userType,
      teacherId: state.teacherId,
      animalCode: state.animalCode,
      studentNumber: state.studentNumber,
      deviceId: state.deviceId,
      sessionExpiry: state.sessionExpiry,
      deviceCount: state.deviceCount,
      maxDevices: state.maxDevices,
      lastTeacherLogin: state.lastTeacherLogin,
      studentLoginEnabled: state.studentLoginEnabled,
      sessionLoading: state.sessionLoading,
      sessionError: state.sessionError,

      // Cast state
      castAppId: state.castAppId,
      castStartedAt: state.castStartedAt,

      // Basic setters
      setUserType: state.setUserType,
      setTeacherId: state.setTeacherId,
      setAnimalCode: state.setAnimalCode,
      setStudentNumber: state.setStudentNumber,
      generateNewAnimalCode: state.generateNewAnimalCode,
      setDeviceCount: state.setDeviceCount,
      setStudentLoginEnabled: state.setStudentLoginEnabled,
      setSessionLoading: state.setSessionLoading,
      setSessionError: state.setSessionError,

      // Firestore-integrated actions
      teacherLoginWithFirestore: state.teacherLoginWithFirestore,
      studentLoginWithFirestore: state.studentLoginWithFirestore,
      regenerateAnimalCodeWithFirestore: state.regenerateAnimalCodeWithFirestore,
      logoutWithFirestore: state.logoutWithFirestore,

      // Cast actions
      startCast: state.startCast,
      stopCast: state.stopCast,
      setCastState: state.setCastState,

      // Legacy actions (local only)
      teacherLogin: state.teacherLogin,
      studentLogin: state.studentLogin,
      sessionLogout: state.sessionLogout,
      isSessionValid: state.isSessionValid,
      refreshSession: state.refreshSession,
      setLastTeacherLogin: state.setLastTeacherLogin,
    }))
  );

export const useWorkspace = () =>
  useAppStore(
    useShallow((state) => ({
      // State
      activeAppId: state.activeAppId,
      recentAppIds: state.recentAppIds,
      favoriteAppIds: state.favoriteAppIds,
      viewMode: state.viewMode,
      savedPresentations: state.savedPresentations,
      // Actions
      setActiveApp: state.setActiveApp,
      addToRecent: state.addToRecent,
      toggleFavorite: state.toggleFavorite,
      addFavorite: state.addFavorite,
      removeFavorite: state.removeFavorite,
      setViewMode: state.setViewMode,
      clearRecentApps: state.clearRecentApps,
      clearFavorites: state.clearFavorites,
      resetWorkspace: state.resetWorkspace,
      addSavedPresentation: state.addSavedPresentation,
      addPDFPresentation: state.addPDFPresentation,
      updateSavedPresentation: state.updateSavedPresentation,
      removeSavedPresentation: state.removeSavedPresentation,
      setSavedPresentations: state.setSavedPresentations,
    }))
  );

export const useClassroomTools = () =>
  useAppStore(
    useShallow((state) => ({
      // Countdown Timer State
      timerDuration: state.timerDuration,
      timerRemaining: state.timerRemaining,
      timerIsRunning: state.timerIsRunning,
      timerStartedAt: state.timerStartedAt,
      timerChimeEnabled: state.timerChimeEnabled,
      timerCompleted: state.timerCompleted,
      // Countdown Timer Actions
      setTimerDuration: state.setTimerDuration,
      setTimerPreset: state.setTimerPreset,
      startTimer: state.startTimer,
      pauseTimer: state.pauseTimer,
      resetTimer: state.resetTimer,
      tickTimer: state.tickTimer,
      syncTimer: state.syncTimer,
      setTimerChimeEnabled: state.setTimerChimeEnabled,
      acknowledgeTimerCompletion: state.acknowledgeTimerCompletion,

      // Work Symbols State
      activeSymbol: state.activeSymbol,
      // Work Symbols Actions
      setActiveSymbol: state.setActiveSymbol,
      toggleSymbol: state.toggleSymbol,
      clearSymbol: state.clearSymbol,

      // Traffic Light State
      trafficLightState: state.trafficLightState,
      // Traffic Light Actions
      setTrafficLight: state.setTrafficLight,
      cycleTrafficLight: state.cycleTrafficLight,

      // Stopwatch State
      stopwatchElapsed: state.stopwatchElapsed,
      stopwatchIsRunning: state.stopwatchIsRunning,
      stopwatchStartedAt: state.stopwatchStartedAt,
      stopwatchLaps: state.stopwatchLaps,
      // Stopwatch Actions
      startStopwatch: state.startStopwatch,
      stopStopwatch: state.stopStopwatch,
      resetStopwatch: state.resetStopwatch,
      recordLap: state.recordLap,
      clearLaps: state.clearLaps,
      tickStopwatch: state.tickStopwatch,
      syncStopwatch: state.syncStopwatch,
    }))
  );

export const useCanvas = () =>
  useAppStore(
    useShallow((state) => ({
      // State
      widgets: state.widgets,
      selectedWidgetId: state.selectedWidgetId,
      maximizedWidgetId: state.maximizedWidgetId,
      canvasMode: state.canvasMode,
      gridSnap: state.gridSnap,
      showGrid: state.showGrid,
      gridSize: state.gridSize,
      backgroundColor: state.backgroundColor,
      backgroundImage: state.backgroundImage,
      savedLayouts: state.savedLayouts,
      currentLayoutId: state.currentLayoutId,
      // Actions
      addCanvasWidget: state.addCanvasWidget,
      updateCanvasWidget: state.updateCanvasWidget,
      removeCanvasWidget: state.removeCanvasWidget,
      selectCanvasWidget: state.selectCanvasWidget,
      bringToFront: state.bringToFront,
      sendToBack: state.sendToBack,
      saveCanvasLayout: state.saveCanvasLayout,
      updateCanvasLayout: state.updateCanvasLayout,
      loadCanvasLayout: state.loadCanvasLayout,
      deleteCanvasLayout: state.deleteCanvasLayout,
      clearCanvas: state.clearCanvas,
      setCanvasMode: state.setCanvasMode,
      toggleGrid: state.toggleGrid,
      toggleGridSnap: state.toggleGridSnap,
      setGridSize: state.setGridSize,
      setCanvasBackground: state.setCanvasBackground,
      setCanvasBackgroundImage: state.setCanvasBackgroundImage,
      resetCanvas: state.resetCanvas,
      setSavedLayouts: state.setSavedLayouts,
      maximizeWidget: state.maximizeWidget,
      restoreWidget: state.restoreWidget,
      toggleMaximize: state.toggleMaximize,
    }))
  );

// Re-export types
export type * from './types';
